import { DEFAULT_ZOOM_RATIO, POPUP_ZOOM_RATIO, SENSOR_STATUS_ABNORMAL, SENSOR_STATUS_ALERT, SENSOR_STATUS_ATTENTION } from 'constants/config';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useAuth } from 'context/AuthContext';
import L from 'leaflet';
import "leaflet/dist/leaflet.css";
import { LockState, SensorType } from 'models';
import PropTypes from 'prop-types';
import React from 'react';
import { GeoJSON, MapContainer, TileLayer } from 'react-leaflet';
import "./MapView.css";
import EmergencyRouteLayer from './EmergencyRouteLayer';
import { useGeoCoordinate } from 'context/GeoCoordinateContext';
import {generateDirectionIcon} from 'utils/helper';
import { useSensors } from 'context/SensorsContext';


const propTypes = {
  markers: PropTypes.array,
  selectedId: PropTypes.string, // id of selected item, this does not need to be one among 'markers'
  zoom: PropTypes.number.isRequired, // current zoom level
  sensorsById: PropTypes.object, // map of sensors by id
  zoom: PropTypes.number.isRequired, // current zoom level
  sensorsById: PropTypes.object, // map of sensors by id
  onViewportChange: PropTypes.func,
  onMarkerClick: PropTypes.func,
  onMapClick: PropTypes.func,
  onMapLoad: PropTypes.func,
};

const defaultProps = {
  markers: [],
  zoom: DEFAULT_ZOOM_RATIO,
  sensorsById: {},
  geoData: null,
  selectedId: '',
  onViewportChange: (e) => {
  },
  onMarkerClick: (marker) => {
  },
  onMarkerMouseOver: (marker) => {
  },
  onMarkerMouseOut: (marker) => {

  },
  onMapClick: () => {
  },
  onMapLoad: (e) => {
  }
};
const MapView = (props) => {
  /*** States and Variables ***/
  const mapRef = React.useRef(null);
  const { sensorGroups } = useSensorGroups();
  const markersRef = React.useRef([]);
  const { generalSetting } = useGeneralSetting();
  const { currentUser } = useAuth();
  const { emergencyRoutes, wardBoundary } = useGeoCoordinate();
  const {sensors} = useSensors();
  
  // Memoize ward boundary style to prevent recreating on every render
  const wardBoundaryBaseStyle = React.useMemo(() => ({
    fillColor: 'transparent',
    pane: 'overlayPane'
  }), []);
  
  // Create a map of sensors by id for quick lookup
  const sensorsMap = React.useMemo(() => {
    const map = new Map();
    sensors.forEach(sensor => {
      map.set(sensor.id, sensor);
    });
    return map;
  }, [sensors]);

  /*** Processing ***/
  React.useEffect(() => {
    if (props.onViewportChange) {
      if (mapRef.current) mapRef.current.on('moveend', props.onViewportChange)
    }
    return () => {
      if (mapRef.current) mapRef.current.off('moveend', props.onViewportChange)
    }
  }, [props.onViewportChange])

  React.useEffect(() => {
    if (props.onMapLoad) {
      if (mapRef.current) mapRef.current.on('load', props.onMapLoad)
    }
    return () => {
      if (mapRef.current) mapRef.current.off('load', props.onMapLoad)
    }
  }, [props.onMapLoad])
  React.useEffect(() => {
    sensorGroups.length && renderMarkers(props.zoom);
    return () => {
      for (const marker of markersRef.current) {
        mapRef.current.removeLayer(marker);
      }
      markersRef.current = [];
    }
  }, [
    props.markers,
    sensorGroups,
    props.onMarkerClick,
    props.onMarkerMouseOut,
    props.zoom,
    currentUser?.data?.displayItems?.cameraDirection
  ])
  /**
   * マップイベントハンドル設定：マップをクリック
   */
  React.useEffect(() => {
    mapRef.current.on('click', onMapClick);

    return () => {
      mapRef.current.off('click', onMapClick);
    }
  }, [props.onMapClick])

  /*** Sub Components ***/

  const getTargetGroup = (value) => sensorGroups.find(g => g.id === value);
  const getTargetMessage = (value, group) => group.messages.find(m => m && m.value === value);
  const isLocked = (value) => value === LockState.LOCKED && generalSetting?.alertOn;

  const checkTypeStatus = (value, groupId) => {
    const targetGroup = getTargetGroup(groupId);
    if (targetGroup) {
      const targetMessage = getTargetMessage(value, targetGroup);
      if (targetMessage) return true;
    }
    return false;
  }

  const modifiedMarker = (marker) => {
    let status, pos, groupId, data;
    //group sensors
    if (marker.id.includes('#')) {
      //alert sensor with locked state
      if (generalSetting?.alertOn) {
        const indexLocked = marker.isLocked.indexOf(LockState.LOCKED);
        if (indexLocked !== -1) {
          groupId = marker.groupId[indexLocked];
          data = marker.data[indexLocked];
          const targetGroup = getTargetGroup(groupId);
          if (targetGroup) {
            status = targetGroup.messages.map(m => m && m.value).indexOf(SENSOR_STATUS_ALERT);
          }
          return {
            status,
            groupId,
            data
          }
        }
      }
      //other state without locked
      const uniqueStatus = new Set(marker.status.filter(statusValue => statusValue !== -1));
      if (uniqueStatus.size) {
        for (const statusValue of uniqueStatus) {
          status = statusValue;
          pos = marker.status.indexOf(statusValue);
          groupId = marker.groupId[pos];
          data = marker.data[pos];
          const isAlert = checkTypeStatus(SENSOR_STATUS_ALERT, groupId);
          if (isAlert) break;

          const isAttension = checkTypeStatus(SENSOR_STATUS_ATTENTION, groupId);
          if (isAttension) break;

          const isAbnormal = checkTypeStatus(SENSOR_STATUS_ABNORMAL, groupId);
          if (isAbnormal) break;
        }
      } else {
        status = -1;
        pos = marker.status.indexOf(status);
        groupId = marker.groupId[pos];
        data = marker.data[pos];
      }
    } else {
      //single sensor
      status = marker.status;
      groupId = marker.groupId;
      data = marker.data;
      if (isLocked(marker.isLocked)) {
        const targetGroup = sensorGroups.find(g => g.id === groupId);
        if (targetGroup) {
          status = targetGroup.messages.map(m => m?.value).indexOf(SENSOR_STATUS_ALERT);
        }
      }
    }

    return {
      status,
      groupId,
      data
    }
  }


  const renderMarkers = (zoom) => {
    props.markers?.forEach((marker) => {
      const sensor = sensorsMap.get(marker.id);
      const camera1Rotation = sensor?.data?.camera1Rotation || 0;
      const camera2Rotation = sensor?.data?.camera2Rotation || 0;
      let color = '#000';
      const { status, groupId, data } = modifiedMarker(marker);
      const targetGroup = sensorGroups.find(g => g.id === groupId);
      if (targetGroup && status !== -1) {
        const targetMessage = targetGroup.messages[status];
        color = targetMessage.color;
      }

      const camera1Direction = sensor?.data?.camera1Direction;
      const camera2Direction = sensor?.data?.camera2Direction;
      const isDisplayDirection = currentUser?.data?.displayItems?.cameraDirection 
          && zoom > POPUP_ZOOM_RATIO && (camera1Direction || camera2Direction) && sensor?.data?.cameraFeature

      const svgIcon = L.divIcon({
        html: targetGroup.type === SensorType.FLOOD ? `
        <svg xmlns="http://www.w3.org/2000/svg" width="40.282" height="46.497" viewBox="0 0 40.282 46.497">
          <g id="icon-sensor-caution" transform="translate(-210.5 -273.5)">
            <path id="パス_24" data-name="パス 24" d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Z" fill="#fff" stroke="${color}" stroke-width="1"/>
            <path id="パス_25" data-name="パス 25" d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Zm5.077,29.95a5.082,5.082,0,0,1-5.077,5.077H220.332a5.082,5.082,0,0,1-5.077-5.077V283.332a5.082,5.082,0,0,1,5.077-5.077H240.95a5.082,5.082,0,0,1,5.077,5.077Z" fill="${color}"/>
            <path id="パス_26" data-name="パス 26" d="M239.884,312.37a7.857,7.857,0,0,0,0,11.111l1.852-1.852a5.244,5.244,0,0,1,0-7.407Zm-1.389-1.389-1.852-1.852a12.439,12.439,0,0,0,0,17.592l1.852-1.852A9.831,9.831,0,0,1,238.5,310.982Zm12.5,1.389-1.852,1.852a5.244,5.244,0,0,1,0,7.407l1.852,1.852A7.857,7.857,0,0,0,250.995,312.37Zm-5.555,3.591a1.964,1.964,0,1,0,1.964,1.964A1.964,1.964,0,0,0,245.439,315.962Zm8.8-6.832-1.852,1.852a9.831,9.831,0,0,1,0,13.888l1.852,1.852A12.439,12.439,0,0,0,254.235,309.13Z" transform="translate(-14.798 -23.63)" fill="${color}"/>
          </g>
        </svg>`: (targetGroup.type === SensorType.FLOOD_GATE || targetGroup.type === SensorType.GENERAL) ?
          `<svg width="39.282" height="45.497" viewBox="0 0 39.282 45.497" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg">
           <g transform="translate(-211 -274)">
            <path d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Z" fill="#fff" />
            <path d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Zm5.077,29.95a5.082,5.082,0,0,1-5.077,5.077H220.332a5.082,5.082,0,0,1-5.077-5.077V283.332a5.082,5.082,0,0,1,5.077-5.077H240.95a5.082,5.082,0,0,1,5.077,5.077Z" fill="${color}" />
          </g>
            <path d="m 30.276154,16.859936 c -1.411003,0 -2.612263,0.968598 -3.057172,2.319178 H 11.723364 c -0.444911,-1.35058 -1.646171,-2.319178 -3.057173,-2.319178 -1.792356,0 -3.241495,1.555214 -3.241495,3.478766 0,1.923555 1.449139,3.478768 3.241495,3.478768 1.411002,0 2.612262,-0.968598 3.057173,-2.319177 h 15.495618 c 0.444909,1.350579 1.646169,2.319177 3.057172,2.319177 1.792357,0 3.241495,-1.555213 3.241495,-3.478768 0,-1.923552 -1.449138,-3.478766 -3.241495,-3.478766 z" fill="${color}" />
        </svg>` : targetGroup.type === SensorType.CAMERA && data === "SINGLE" ? `<svg xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape" xmlns:sodipodi="http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg" width="39.282" height="45.497" viewBox="0 0 39.282 45.497" version="1.1" id="svg6" sodipodi:docname="icon-camera.svg" xml:space="preserve" inkscape:version="1.2.2 (b0a84865, 2022-12-01)" inkscape:export-filename="icon-camera.png" inkscape:export-xdpi="96" inkscape:export-ydpi="96" style="overflow:visible">
          <defs id="defs10"/>
          <sodipodi:namedview id="namedview8" pagecolor="#ffffff" bordercolor="#000000" borderopacity="0.25" inkscape:showpageshadow="2" inkscape:pageopacity="0.0" inkscape:pagecheckerboard="0" inkscape:deskcolor="#d1d1d1" showgrid="false" inkscape:zoom="1.2967888" inkscape:cx="-26.218611" inkscape:cy="-98.705359" inkscape:window-width="1950" inkscape:window-height="1170" inkscape:window-x="319" inkscape:window-y="28" inkscape:window-maximized="0" inkscape:current-layer="icon-sensor-danger"/>
          <g id="icon-sensor-danger" transform="translate(-211 -274)"><path id="パス_24" data-name="パス 24" d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Z" fill="#fff"/>
          <path id="パス_25" data-name="パス 25" d="M240.95,274H220.332A9.332,9.332,0,0,0,211,283.332V303.95a9.332,9.332,0,0,0,9.332,9.332h5.773l3.115,5.4a1.64,1.64,0,0,0,2.841,0l3.115-5.4h5.773a9.332,9.332,0,0,0,9.332-9.332V283.332A9.332,9.332,0,0,0,240.95,274Zm5.077,29.95a5.082,5.082,0,0,1-5.077,5.077H220.332a5.082,5.082,0,0,1-5.077-5.077V283.332a5.082,5.082,0,0,1,5.077-5.077H240.95a5.082,5.082,0,0,1,5.077,5.077Z" fill="#de3517" style="fill:${color}"/></g>
          <path d="m 30.386257,17.86698 v 3.780949 h -6.33435 V 18.93825 L 29.251306,17.344265 27.30914,9.0526505 C 26.838745,7.3143597 25.44271,6.7264249 24.273609,7.0474899 l -17.0708044,5.2328281 1.795652,2.804202 -2.3472914,0.719642 1.4255061,6.086691 4.4142167,-1.352951 c 0.910769,1.523723 1.979348,1.291826 2.497386,1.179028 l 4.106313,-1.260317 v 1.191316 h -1.65244 v 3.261694 c 0,1.087965 0.508126,1.71655 1.445335,1.87535 l 11.498775,1.681574 v 3.894061 h 1.65244 V 17.86698 Z m -21.1410364,1.708674 -0.570366,-2.433356 1.3712484,-0.420315 1.396862,2.181292 z m 5.3897064,0.292392 c -0.283667,0.07877 -0.480583,0.04725 -0.660975,-0.213306 L 9.9626556,13.392862 24.701315,8.874632 c 0.480584,-0.1461917 0.879648,0.1783345 1.011844,0.6685951 l 1.514734,6.4657339 z m 6.057019,0.10052 1.762601,-0.54036 v 2.219732 h -1.762601 z m -1.597359,5.098607 v -1.686304 h 11.29167 v 3.337316 z" id="path316" style="stroke-width:0.0294574;fill:${color}"/>
          ${isDisplayDirection ? generateDirectionIcon(data, 'string', color, camera1Rotation, camera2Rotation, camera1Direction, camera2Direction) : ''}
          </svg>` : `<svg xmlns="http://www.w3.org/2000/svg" width="39" height="45" viewBox="0 0 39 45" fill="none" style="overflow:visible">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M8 4H31C33.2091 4 35 5.79086 35 8V31C35 33.2091 33.2091 35 31 35H8C5.79086 35 4 33.2091 4 31V8C4 5.79086 5.79086 4 8 4Z" fill="white"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0 8C0 3.58172 3.58172 0 8 0H31C35.4183 0 39 3.58172 39 8V31C39 35.4183 35.4183 39 31 39H23.6259L19.8956 44.5207C19.4637 45.1598 18.5363 45.1598 18.1044 44.5207L14.3741 39H8C3.58172 39 0 35.4183 0 31V8ZM8 4H31C33.2091 4 35 5.79086 35 8V31C35 33.2091 33.2091 35 31 35H8C5.79086 35 4 33.2091 4 31V8C4 5.79086 5.79086 4 8 4Z" fill="${color}"/>
        <path d="M8.41738 33.2478H6.43517V27.7813H8.43376C8.99621 27.7813 9.48038 27.8907 9.88629 28.1096C10.2922 28.3267 10.6044 28.639 10.8228 29.0465C11.043 29.454 11.1532 29.9415 11.1532 30.5092C11.1532 31.0786 11.043 31.568 10.8228 31.9773C10.6044 32.3865 10.2904 32.7006 9.88083 32.9195C9.4731 33.1384 8.98528 33.2478 8.41738 33.2478ZM7.61739 32.2575H8.36823C8.71771 32.2575 9.01168 32.197 9.25013 32.076C9.49039 31.9532 9.67059 31.7637 9.79073 31.5075C9.91268 31.2495 9.97366 30.9167 9.97366 30.5092C9.97366 30.1053 9.91268 29.7752 9.79073 29.5189C9.67059 29.2627 9.4913 29.074 9.25286 28.953C9.01441 28.832 8.72044 28.7715 8.37096 28.7715H7.61739V32.2575Z" fill="${color}"/>
        <path d="M14.6964 31.5021V29.1479H15.8595V33.2478H14.7428V32.5031H14.6992C14.6045 32.7433 14.4471 32.9364 14.2268 33.0823C14.0084 33.2282 13.7417 33.3012 13.4268 33.3012C13.1465 33.3012 12.8999 33.2389 12.6869 33.1143C12.474 32.9898 12.3074 32.8127 12.1873 32.5832C12.069 32.3536 12.0089 32.0787 12.0071 31.7584V29.1479H13.1702V31.5555C13.172 31.7975 13.2384 31.9888 13.3695 32.1294C13.5006 32.27 13.6762 32.3403 13.8964 32.3403C14.0366 32.3403 14.1677 32.3091 14.2896 32.2469C14.4116 32.1828 14.5099 32.0885 14.5845 31.9639C14.6609 31.8394 14.6983 31.6854 14.6964 31.5021Z" fill="${color}"/>
        <path d="M17.9858 33.3252C17.7182 33.3252 17.4798 33.2798 17.2704 33.1891C17.0611 33.0966 16.8955 32.9604 16.7735 32.7807C16.6534 32.5992 16.5933 32.3732 16.5933 32.1027C16.5933 31.8749 16.6361 31.6836 16.7216 31.5288C16.8072 31.374 16.9237 31.2495 17.0711 31.1551C17.2186 31.0608 17.386 30.9897 17.5735 30.9416C17.7628 30.8936 17.9612 30.8598 18.1687 30.8402C18.4126 30.8153 18.6092 30.7921 18.7585 30.7708C18.9077 30.7476 19.016 30.7138 19.0834 30.6693C19.1507 30.6249 19.1844 30.559 19.1844 30.4718V30.4558C19.1844 30.2868 19.1298 30.156 19.0206 30.0634C18.9132 29.9709 18.7603 29.9246 18.5619 29.9246C18.3526 29.9246 18.186 29.97 18.0622 30.0608C17.9385 30.1497 17.8565 30.2618 17.8165 30.3971L16.7408 30.3117C16.7954 30.0625 16.9028 29.8472 17.0629 29.6657C17.2231 29.4824 17.4297 29.3419 17.6827 29.244C17.9375 29.1443 18.2324 29.0945 18.5673 29.0945C18.8003 29.0945 19.0233 29.1212 19.2363 29.1746C19.4511 29.228 19.6413 29.3107 19.8069 29.4228C19.9744 29.5349 20.1063 29.6791 20.2028 29.8552C20.2993 30.0296 20.3475 30.2387 20.3475 30.4825V33.2478H19.2445V32.6793H19.2117C19.1443 32.8074 19.0542 32.9204 18.9414 33.0183C18.8285 33.1143 18.6929 33.19 18.5346 33.2451C18.3762 33.2985 18.1933 33.3252 17.9858 33.3252ZM18.3189 32.5405C18.49 32.5405 18.6411 32.5075 18.7721 32.4417C18.9032 32.3741 19.006 32.2833 19.0806 32.1694C19.1553 32.0556 19.1926 31.9265 19.1926 31.7824V31.3473C19.1562 31.3705 19.1061 31.3918 19.0424 31.4114C18.9805 31.4292 18.9105 31.4461 18.8322 31.4621C18.7539 31.4763 18.6756 31.4897 18.5974 31.5021C18.5191 31.5128 18.4481 31.5226 18.3844 31.5315C18.2479 31.5511 18.1287 31.5822 18.0267 31.6249C17.9248 31.6676 17.8456 31.7255 17.7892 31.7984C17.7328 31.8696 17.7046 31.9586 17.7046 32.0653C17.7046 32.2202 17.7619 32.3385 17.8766 32.4204C17.9931 32.5004 18.1405 32.5405 18.3189 32.5405Z" fill="${color}"/>
        <path d="M22.413 27.7813V33.2478H21.2499V27.7813H22.413Z" fill="${color}"/>
        <path d="M9.94043 14.9732L9.14305 13.8134C8.76613 13.2652 9.01969 12.5086 9.65086 12.2982L24.5102 7.34506C25.0277 7.17258 25.5879 7.44658 25.7695 7.96093L28.2113 14.8793C28.3946 15.3988 28.1233 15.9686 27.6046 16.1539L23.8922 17.4797M9.94043 14.9732L8.36963 15.8015C7.83377 16.084 7.6702 16.7743 8.0223 17.2673L9.45475 19.2727C9.73592 19.6664 10.2585 19.8023 10.6959 19.5956L12.5241 18.7313M9.94043 14.9732L12.5241 18.7313M12.5241 18.7313L13.6384 20.3522C13.8954 20.7259 14.3717 20.8799 14.7988 20.7274L20.0005 18.8696M20.0005 18.8696V21.5363C20.0005 22.0886 20.4483 22.5363 21.0005 22.5363H22.8922C23.4445 22.5363 23.8922 22.0886 23.8922 21.5363V17.4797M20.0005 18.8696L23.8922 17.4797M31.6432 22.5363H19.3756C18.8233 22.5363 18.3756 22.9841 18.3756 23.5363V24.3267C18.3756 24.7914 18.6956 25.1948 19.1481 25.3005L30.8708 28.0399C31.3232 28.1456 31.6432 28.549 31.6432 29.0137V31.8375V17.8857" stroke="${color}" stroke-width="1.5"/>
        ${isDisplayDirection ? generateDirectionIcon(data, 'string', color, camera1Rotation, camera2Rotation, camera1Direction, camera2Direction) : ''}
        </svg>`,
        className: "map_view_marker",
        iconSize: [39, 45],
        iconAnchor: [12, 40],
      });
      markersRef.current
        .push(
          L.marker([marker.lat, marker.lng], { icon: svgIcon })
            .addTo(mapRef.current)
            .on('click', () => {
              props.onMarkerClick(marker)
            })
            .on('mouseover', () => {
              props.onMarkerMouseOver(marker)
            })
            .on('mouseout', () => {
              props.onMarkerMouseOut(marker)
            })
        );
    })
  }

  /*** Event Handlers ***/
  const onMapReady = (e) => {
    const map = e.target;
    mapRef.current = map;
    map.on('click', onMapClick);
    if (props.onViewportChange) {
      map.on('moveend', props.onViewportChange)
    }
    props.onMapLoad(e);
  }
  
  const isCityBoundaryDisplay = props.geoData && currentUser?.data?.displayItems?.cityBoundary;
  const isWardBoundaryDisplay = wardBoundary && currentUser?.data?.displayItems?.wardBoundary;
  const isEmergencyRoute1Display = emergencyRoutes && currentUser?.data?.displayItems?.emergencyRoute1;
  const isEmergencyRoute2Display = emergencyRoutes && currentUser?.data?.displayItems?.emergencyRoute2;
  const isCivilOfficeDirectorRoute = emergencyRoutes && currentUser?.data?.displayItems?.civilOfficeDirectorRoute;

  /**
   * イベントハンドル：マップをクリック
   * @param {event} e
   */
  const onMapClick = (e) => {
    props.onMapClick();
  }
  /*** Main Render ***/
  // Get user's initial coordinates or use default
  const getUserInitialCenter = () => {
    const userLat = currentUser?.data?.latitude;
    const userLng = currentUser?.data?.longitude;
    
    if (userLat && userLng && !isNaN(Number(userLat)) && !isNaN(Number(userLng))) {
      return [Number(userLat), Number(userLng)];
    }
    return [36.83763081714207, 139.3154172350928];
  };

  return <div className='map_view_map_container'>
    <MapContainer
      center={getUserInitialCenter()}
      zoom={5}
      scrollWheelZoom={true} 
      style={{ height: '100%', zIndex: 0 }}
      zoomControl={false}
      zoomDelta={0.5}
      zoomSnap={0.25}
      whenReady={onMapReady}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {/* city boundary */}
      {isCityBoundaryDisplay && (
        <GeoJSON ref={props.geoJsonRef} data={props.geoData.geojson} key={props.geoData.place_id} style={{ fillColor: 'transparent', color: generalSetting.borderSettings[0].color }} pane="overlayPane" />
      )}

      {/* ward boundary */}
      {isWardBoundaryDisplay && wardBoundary?.map((boundary) => {
        const boundaryData = boundary?.data;
        if (!boundaryData?.geojson || !boundaryData?.placeId) return null;
        
        return (
          <GeoJSON
            ref={props.geoJsonRef} 
            data={boundaryData.geojson} 
            key={`ward-boundary-${boundaryData.placeId}`}
            style={{
              ...wardBoundaryBaseStyle,
              color: boundary.color
            }}
          />
        );
      })}

      {/* emergency routes */}
      {isEmergencyRoute1Display && <EmergencyRouteLayer coordinates={emergencyRoutes['第一次緊急輸送路'] || []} color="#0095D8" />}
      {isEmergencyRoute2Display && <EmergencyRouteLayer coordinates={emergencyRoutes['第二次緊急輸送路'] || []} color="#1C994F" />}
      {isCivilOfficeDirectorRoute && <EmergencyRouteLayer coordinates={emergencyRoutes['土木事務所長選定路線'] || []} color="#DE9E8B" />}
      
    </MapContainer>
  </div>;
}
MapView.propTypes = propTypes;
MapView.defaultProps = defaultProps;
export default MapView;